/**
 * Labour rates — the wire shape for the one financial input that had no route.
 *
 * ## Why this file exists (TRBUD-93 / TICKET-815)
 *
 * `set_labour_rate` was built by migration 0045 and nothing in the running
 * application ever called it. The only caller in the whole repo was
 * `apps/api/src/seed-demo.ts`, so on any environment that was not demo-seeded no
 * mechanic had a rate — and `post_time_entry` raises *'no effective labour
 * rate'* without one. The entire internal-labour path, manual entry and the
 * automatic stage clock alike, could post nothing. A cold start, not a
 * correction gap.
 *
 * ## Who may
 *
 * `setLabourRate` in `permissions.ts` — admin and fleet manager (Eden,
 * 1 September 2026). Migration 0106 widened the database function's own role
 * check to match, and the two have to stay in step: an API that admits a manager
 * over a database that refuses one is a control that appears and then fails,
 * which is worse than no control at all.
 *
 * ## The rate is a VALUATION POLICY, not a wage
 *
 * No wage column exists anywhere in TruckBuddy by construction (db-invariants
 * 1.11, 14). The figure sits deliberately between a mechanic's pay and a shop's
 * billing rate, so in-house work can be compared against its outsourced
 * alternative without the database ever holding an HR number. The screen says
 * so, and this file is where the sentence lives so the API and the UI cannot
 * word it differently.
 */
import { z } from "zod";
import { RATE_SCALE } from "./money.js";
import { currencyCodeDefaulted, strictObject } from "./requests.js";
import type { StoredCurrency, UserRole } from "./vocabulary.js";

/**
 * An hourly rate on the wire — a decimal STRING, never a JS number.
 *
 * Deliberately NOT `decimalString` from `requests.ts`, and the coordinate bug
 * two doors down is why: that one allows at most **two** decimal places because
 * it exists for ledger amounts, while `labour_rate.hourly_rate` is
 * `numeric(10,4)`. Reusing it would not round `62.5125` — being a regex, it
 * would REJECT it outright, and the sub-cent precision that makes
 * hours × rate land on the right cent would be unreachable through the API.
 *
 * Six integer digits, because `numeric(10,4)` leaves exactly that many.
 */
export const hourlyRateString = z
  .string()
  .trim()
  .regex(
    /^\d{1,6}(\.\d{1,4})?$/,
    `expected an hourly rate with at most ${RATE_SCALE} decimal places`,
  )
  .refine((value) => Number.parseFloat(value) > 0, "a labour rate must be greater than zero");

/**
 * Set someone's rate.
 *
 * `effectiveFrom` is optional and means *now* when omitted, matching the
 * function's `COALESCE(p_effective_from, now())`. It exists because a rate
 * decided in a meeting on Monday and entered on Thursday should be able to say
 * so.
 *
 * The rule the database applies to it, since migration 0144, is one sentence:
 * **coverage may be EXTENDED where there was none, and never rewritten where it
 * already exists.** So an instant before the earliest period this person has
 * ever had is accepted — nothing was covered there, so no posted hour can be
 * re-valued — while an instant landing inside a period that has already CLOSED
 * is still refused, because `cost_entry.posted_rate` is frozen at posting time
 * and amending that history would silently re-value hours already paid for.
 *
 * That first half is not a convenience. Without it, work done before the rate
 * was configured could never be logged and could never be reached by
 * back-dating either — a cold-start softlock that recurred for every new hire
 * (F-11 / TRBUD-104).
 *
 * **No `idempotencyKey`, and that is not an oversight.** `set_labour_rate`
 * returns the existing period's id unchanged when the request asserts something
 * ALREADY TRUE — the same rate and currency, from an instant at or after the
 * open period's start — so a double-submit is a no-op by construction rather
 * than by a replay cache. Adding a key would put a second, weaker guard in
 * front of a stronger one.
 *
 * "Already true" is the test, and F-42 (TRBUD-143) is what it replaced. The
 * no-op branch used to compare the figure alone and return before every later
 * check, so re-asserting the same rate from an EARLIER instant answered 200 with
 * the id of the period that already started later, moved nothing, and said
 * nothing — while the identical request at a different figure was refused
 * loudly, so the operator read 200 as success. An earlier instant is not already
 * true, and now acts or refuses. A later one is, and still records nothing.
 */
export const setLabourRateBody = strictObject({
  hourlyRate: hourlyRateString,
  currency: currencyCodeDefaulted,
  effectiveFrom: z.string().datetime({ offset: true }).nullish(),
});
export type SetLabourRateBody = z.output<typeof setLabourRateBody>;

/** One person, and the rate their hours are currently valued at. */
export interface LabourRateRow {
  userId: string;
  displayName: string;
  email: string;
  roles: UserRole[];
  /**
   * The open period's rate as a scale-4 decimal string, or **null for somebody
   * who has never been given one**.
   *
   * Null is the state this whole feature exists for, so it is a first-class
   * value rather than a zero: a mechanic with no rate cannot log time at all,
   * and the screen has to be able to say that rather than render `0.00` and
   * imply their work is free.
   */
  hourlyRate: string | null;
  /**
   * The rate's own code. New rates are USD only (TRBUD-385), and the Labour
   * Rates screen sets a rate in USD whatever this says; an older MXN or CAD rate
   * on a staging or developer database still reads in its own code. Null keeps
   * its own meaning: nobody has ever set one.
   */
  currency: StoredCurrency | null;
  /** When the open period started. Null exactly when `hourlyRate` is null. */
  effectiveFrom: string | null;
  /** Display name of whoever set it — the ACTOR, not the subject. */
  setBy: string | null;
}

/**
 * How much labour was priced on a substituted rate — TRBUD-148 / F-11.
 *
 * The audit half of the basis column. `cost_entry.pricing_basis` records a
 * wave-through, and CLAUDE.md's cold-start rule calls it *"the only way to ask
 * afterwards how much went unchecked"* — but until this existed the only way to
 * ask was a SQL client, so in practice nobody asked and the accountability half
 * of the rule was decorative.
 *
 * It rides on the labour-rates response because that is where the cause lives: a
 * non-zero count here means somebody's rate periods do not cover work that was
 * posted against them, and the fix is to set the missing rate.
 */
export interface SubstitutedPricing {
  /** Cost entries priced on a recorded basis rather than the normal one. */
  entries: number;
  /** Their total, so the figure is money and not just a tally. */
  amount: string;
  currency: string;
}

export interface LabourRatesResponse {
  rows: LabourRateRow[];
  /** False when the caller may look but not change. The route refuses either way. */
  canEdit: boolean;
  /**
   * Null when nothing was ever substituted — which is the honest cold-start
   * answer and must render as "none", never as a zero that looks measured when
   * no labour has been posted at all.
   */
  substituted: SubstitutedPricing | null;
}
